import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/redux/store';
import DropdownPopover from 'src/components/UI/DropdownPopover';
import ReviewAutomationBreadcrumbs from './ReviewAutomationBreadcrumbs';
import { useGetUserAgentDetails } from '../../hooks/useGetUserAgentDetails';
import { useGetAgentsStatus } from '../../hooks/useGetAgentsStatus';
import { getProfileDisabledState } from '../../hooks/useIsConfiguredToOtherSite';
import useSiteGBP1to1Mapping, { IGBPLocation } from '../../hooks/useSiteGBP1to1Maping';
import AgentLastRun from '../../Components/AgentLastRun';

interface ReviewAutomationCommonProps {
  onSelect?: (profile: IGBPLocation) => void;
  selectedProfile?: IGBPLocation | null;
  lastRunTime?: string | number | null;
}

function ReviewAutomationCommon({
  onSelect,
  selectedProfile: propSelectedProfile,
  lastRunTime
}: ReviewAutomationCommonProps) {
  const dispatch = useDispatch();

  const { _id: siteId } = useSelector(
    (state: RootState) => state?.siteAudit?.siteAuditDetails
  ) ?? {};

  const siteGBP1to1Configurations = useSiteGBP1to1Mapping({ siteId });

  const { data: userAgentDetails } = useGetUserAgentDetails(siteId);
  const { data: agentsStatus } = useGetAgentsStatus(siteId);
  const localSeoApiData = userAgentDetails?.response?.local_seo;
  const reviewAutomationApiData = userAgentDetails?.response?.review_automation;

  const lastRunTimestamp = lastRunTime || null;

  const isOptionDisabled = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).disabled;

  const getOptionDisabledTooltip = (profile: any) =>
    getProfileDisabledState(profile, localSeoApiData, siteId, siteGBP1to1Configurations.connectedSite?.url, siteGBP1to1Configurations.siteList, reviewAutomationApiData).tooltip;

  const effectiveSelectedProfile = useMemo(() => {
    if (propSelectedProfile) return propSelectedProfile;
    if (!siteGBP1to1Configurations || siteGBP1to1Configurations.isConfigurationError) return null;
    return siteGBP1to1Configurations.connectedGBPAccount ?? null;
  }, [propSelectedProfile, siteGBP1to1Configurations]);

  useEffect(() => {
    if (onSelect && effectiveSelectedProfile && !propSelectedProfile) {
      onSelect(effectiveSelectedProfile);
    }
  }, [effectiveSelectedProfile, onSelect, propSelectedProfile]);

  const handleProfileSelect = (location: IGBPLocation) => {
    if (onSelect) {
      onSelect(location);
    }
  };

  return (
    <div className="flex justify-between items-center mb-3 w-full">
      <div>
        <ReviewAutomationBreadcrumbs />
      </div>
      <div className="flex gap-4 items-center">
        <AgentLastRun timestamp={lastRunTimestamp} showBorder />
        {siteGBP1to1Configurations.GBPLocations?.length > 0 && (
          <div aria-label="google-business-profile" style={{ minWidth: 200 }}>
            <DropdownPopover
              startIcon="/static/images/icons/local-seo/google-home.svg"
              selectedValue={effectiveSelectedProfile}
              options={siteGBP1to1Configurations.GBPLocations || []}
              onSelect={handleProfileSelect}
              isArrayOfObject={true}
              labelKey="title"
              idKey="name"
              placeholder="Select Business"
              enableSearch={siteGBP1to1Configurations.GBPLocations?.length > 5}
              searchPlaceholder="Search profiles..."
              isOptionDisabled={isOptionDisabled}
              getOptionDisabledTooltip={getOptionDisabledTooltip}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewAutomationCommon;
